import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  useTheme, 
  Button, 
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Autocomplete,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import { tokens } from '../../theme';
import { getToken, base_url } from '../../config/config';
import axios from 'axios';
import { 
  LocalShippingOutlined,
  ProductionQuantityLimitsOutlined,
  AddCircleOutlineOutlined,
  SearchOutlined,
  CheckCircle,
  Pending,
  Cancel,
  SwapHoriz
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import StockMovementsList from './components/StockMovementsList';

const AnrStockList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { anrId } = useParams();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [projectData, setProjectData] = useState(null);
  const [stockMovements, setStockMovements] = useState([]);
  const [createMovementDialog, setCreateMovementDialog] = useState(false);
  const [products, setProducts] = useState([]);
  const [newMovement, setNewMovement] = useState({
    productId: '',
    quantity: 1,
    fromLocation: 'stock',
    toLocation: 'production',
    anrId: anrId,
    state: 'draft'
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (anrId) {
      fetchData();
    }
  }, [anrId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      
      // Fetch project data
      const projectResponse = await axios.post(`${base_url}/v1/getProjectbyANR`, 
        { ANR: anrId },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      if (projectResponse.data.success && projectResponse.data.data.length > 0) {
        setProjectData(projectResponse.data.data[0]);
      }
      
      // Fetch stock movements for this ANR
      const movementsResponse = await axios.get(`${base_url}/v1/stock/moves/anr/${anrId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (movementsResponse.data.success) {
        setStockMovements(movementsResponse.data.data);
      }
      
      // Fetch all products for the movement dialog
      const productsResponse = await axios.get(`${base_url}/v1/stock/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (productsResponse.data.success) {
        setProducts(productsResponse.data.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setAlert({
        open: true,
        message: 'Failed to load project data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCreateMovementDialogOpen = () => {
    setCreateMovementDialog(true);
  };

  const handleCreateMovementDialogClose = () => {
    setCreateMovementDialog(false);
    setSelectedProduct(null);
    setNewMovement({
      productId: '',
      quantity: 1,
      fromLocation: 'stock',
      toLocation: 'production',
      anrId: anrId,
      state: 'draft'
    });
  };

  const handleMovementInputChange = (e) => {
    const { name, value } = e.target;
    setNewMovement({
      ...newMovement,
      [name]: value
    });
  };

  const handleProductSelect = (event, product) => {
    setSelectedProduct(product);
    if (product) {
      setNewMovement({
        ...newMovement,
        productId: product._id
      });
    } else {
      setNewMovement({
        ...newMovement,
        productId: ''
      });
    }
  };

  const handleCreateStockMovement = async () => {
    try {
      const token = getToken();
      const response = await axios.post(`${base_url}/v1/stock/moves`, newMovement, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStockMovements([response.data.data, ...stockMovements]);
        setAlert({
          open: true,
          message: 'Stock movement created successfully',
          severity: 'success'
        });
        handleCreateMovementDialogClose();
        
        // If the state is "done", refresh project data to get updated material costs
        if (newMovement.state === 'done') {
          const projectResponse = await axios.post(`${base_url}/v1/getProjectbyANR`, 
            { ANR: anrId },
            { headers: { 'Authorization': `Bearer ${token}` }}
          );
          
          if (projectResponse.data.success && projectResponse.data.data.length > 0) {
            setProjectData(projectResponse.data.data[0]);
          }
        }
      }
    } catch (error) {
      console.error('Error creating stock movement:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Failed to create stock movement',
        severity: 'error'
      });
    }
  };

  const locationOptions = [
    { value: 'supplier', label: 'Supplier' },
    { value: 'production', label: 'Production' },
    { value: 'quality', label: 'Quality Control' },
    { value: 'stock', label: 'Stock' },
    { value: 'customer', label: 'Customer' }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!projectData) {
    return (
      <Box m="20px">
        <Typography variant="h3" color={colors.redAccent[500]}>
          Project with ANR {anrId} not found
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/stock')}
          sx={{ mt: 2 }}
        >
          Back to Stock Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h2" color={colors.grey[100]} fontWeight="bold" mb="5px">
            Stock for ANR: {anrId}
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            {projectData.Auftragsbezeichnung || "No project description"}
          </Typography>
        </Box>
        
        <Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddCircleOutlineOutlined />}
            onClick={handleCreateMovementDialogOpen}
            sx={{ mr: 1 }}
          >
            Create Movement
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<LocalShippingOutlined />}
            onClick={() => navigate('/stock/movements')}
          >
            All Movements
          </Button>
        </Box>
      </Box>

      {/* TABS */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          textColor="secondary"
          indicatorColor="secondary"
        >
          <Tab label="Material Costs" />
          <Tab label="Stock Movements" />
        </Tabs>
      </Box>

      {/* MATERIAL COSTS TAB */}
      {tabValue === 0 && (
        <Box mt={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h4">Material Costs</Typography>
            <Typography variant="h5">
              Total Material Cost: €{projectData.totalMaterialCost || '0.00'}
            </Typography>
          </Box>
          
          {projectData.materialCosts && projectData.materialCosts.length > 0 ? (
            <TableContainer component={Paper} sx={{ bgcolor: colors.primary[400] }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Unit</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectData.materialCosts.map((material, index) => (
                    <TableRow 
                      key={material._id || index}
                      sx={{ '&:hover': { bgcolor: colors.primary[500] } }}
                    >
                      <TableCell>{material.name}</TableCell>
                      <TableCell>{material.Artikelnummer}</TableCell>
                      <TableCell>{material.description}</TableCell>
                      <TableCell>{material.unitName}</TableCell>
                      <TableCell>{material.units}</TableCell>
                      <TableCell>€{material.singleCost}</TableCell>
                      <TableCell>€{material.TotalMaterialCost}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center"
              bgcolor={colors.primary[400]}
              p={4}
              borderRadius={1}
            >
              <Box textAlign="center">
                <ProductionQuantityLimitsOutlined sx={{ fontSize: 60, color: colors.grey[500], mb: 2 }} />
                <Typography variant="h5" color={colors.grey[300]}>
                  No material costs found for this project
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddCircleOutlineOutlined />}
                  onClick={handleCreateMovementDialogOpen}
                  sx={{ mt: 2 }}
                >
                  Add Materials
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* STOCK MOVEMENTS TAB */}
      {tabValue === 1 && (
        <Box mt={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h4">Stock Movements</Typography>
            <Typography variant="h6">
              Total: {stockMovements.length} movements
            </Typography>
          </Box>
          
          {stockMovements.length > 0 ? (
            <Paper sx={{ bgcolor: colors.primary[400], p: 2 }}>
              <StockMovementsList movements={stockMovements} />
            </Paper>
          ) : (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center"
              bgcolor={colors.primary[400]}
              p={4}
              borderRadius={1}
            >
              <Box textAlign="center">
                <LocalShippingOutlined sx={{ fontSize: 60, color: colors.grey[500], mb: 2 }} />
                <Typography variant="h5" color={colors.grey[300]}>
                  No stock movements found for this ANR
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddCircleOutlineOutlined />}
                  onClick={handleCreateMovementDialogOpen}
                  sx={{ mt: 2 }}
                >
                  Create Movement
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* CREATE MOVEMENT DIALOG */}
      <Dialog open={createMovementDialog} onClose={handleCreateMovementDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Create Stock Movement for ANR: {anrId}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Autocomplete
              options={products}
              getOptionLabel={(option) => `${option.name} (${option.artikelNummer})`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Product"
                  variant="outlined"
                  fullWidth
                  required
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchOutlined />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
              value={selectedProduct}
              onChange={handleProductSelect}
              sx={{ mb: 2 }}
            />

            {selectedProduct && (
              <Box mb={2} p={2} bgcolor={colors.primary[500]} borderRadius={1}>
                <Typography variant="body2" color={colors.grey[300]}>
                  Current Stock: {selectedProduct.stockQuantity} {selectedProduct.unitName}
                </Typography>
                {selectedProduct.stockQuantity <= selectedProduct.minStockLevel && (
                  <Chip 
                    label="Low Stock" 
                    size="small" 
                    color="error" 
                    sx={{ mt: 1 }} 
                  />
                )}
              </Box>
            )}

            <TextField
              label="Quantity"
              name="quantity"
              type="number"
              value={newMovement.quantity}
              onChange={handleMovementInputChange}
              fullWidth
              required
              InputProps={{
                inputProps: { min: 1 }
              }}
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={5}>
                <FormControl fullWidth required>
                  <InputLabel>From Location</InputLabel>
                  <Select
                    name="fromLocation"
                    value={newMovement.fromLocation}
                    onChange={handleMovementInputChange}
                    label="From Location"
                  >
                    {locationOptions.map(option => (
                      <MenuItem 
                        key={option.value} 
                        value={option.value}
                        disabled={option.value === newMovement.toLocation}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={2} display="flex" justifyContent="center" alignItems="center">
                <SwapHoriz />
              </Grid>
              
              <Grid item xs={5}>
                <FormControl fullWidth required>
                  <InputLabel>To Location</InputLabel>
                  <Select
                    name="toLocation"
                    value={newMovement.toLocation}
                    onChange={handleMovementInputChange}
                    label="To Location"
                  >
                    {locationOptions.map(option => (
                      <MenuItem 
                        key={option.value} 
                        value={option.value}
                        disabled={option.value === newMovement.fromLocation}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                name="state"
                value={newMovement.state}
                onChange={handleMovementInputChange}
                label="Status"
              >
                <MenuItem value="draft">
                  <Box display="flex" alignItems="center">
                    <Pending sx={{ mr: 1, color: colors.grey[500] }} />
                    Draft
                  </Box>
                </MenuItem>
                <MenuItem value="assigned">
                  <Box display="flex" alignItems="center">
                    <Pending sx={{ mr: 1, color: colors.blueAccent[500] }} />
                    Assigned
                  </Box>
                </MenuItem>
                <MenuItem value="done">
                  <Box display="flex" alignItems="center">
                    <CheckCircle sx={{ mr: 1, color: colors.greenAccent[500] }} />
                    Done
                  </Box>
                </MenuItem>
                <MenuItem value="cancelled">
                  <Box display="flex" alignItems="center">
                    <Cancel sx={{ mr: 1, color: colors.redAccent[500] }} />
                    Cancelled
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body2" color={colors.grey[300]}>
              Note: Setting status to "Done" will immediately update stock levels and project material costs.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateMovementDialogClose}>Cancel</Button>
          <Button 
            onClick={handleCreateStockMovement} 
            variant="contained" 
            color="secondary"
            disabled={!newMovement.productId || newMovement.quantity < 1}
          >
            Create Movement
          </Button>
        </DialogActions>
      </Dialog>

      {/* ALERT SNACKBAR */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AnrStockList;